import type { JoinRequest } from '../services/api/joinRequests';

/**
 * MKS-279 — how long a DECIDED join request stays on the requester's "My
 * Project Join Requests" table. Pending ones are shown however old they are:
 * an unanswered request is never stale news.
 *
 * The window is applied client-side rather than on the endpoint deliberately,
 * for the same reason `MyBackdatedHoursRequests.tsx` applies its own: the route
 * returns only the caller's OWN rows and a person has a handful of them, so a
 * server-side window would buy nothing and would change the route schema, the
 * OpenAPI snapshot and the route-table baseline.
 *
 * Shorter than the backdated-hours section's 60 days on purpose. A join request
 * is decided once and then never acted on again, so a week is all the notice
 * the requester needs; a backdated-hours decision can send someone back to fix
 * a timesheet, which is worth keeping around far longer.
 */
export const JOIN_REQUEST_RECENT_DAYS = 7;

const WINDOW_MS = JOIN_REQUEST_RECENT_DAYS * 24 * 60 * 60 * 1000;

/** `updatedAt` is set when the request is decided, so it IS the decision time. */
function isRecentlyDecided(req: JoinRequest, now: number): boolean {
  if (!req.updatedAt) return true;
  const decidedAt = Date.parse(req.updatedAt);
  // A missing or unparseable timestamp must not hide a row — showing one row
  // too many is a far smaller failure than silently swallowing a decision the
  // requester has not seen yet.
  if (Number.isNaN(decidedAt)) return true;
  return now - decidedAt <= WINDOW_MS;
}

/**
 * Pending requests always, decided ones only for `JOIN_REQUEST_RECENT_DAYS`
 * after the decision. `now` is passed in so the caller owns the clock.
 */
export function filterVisibleJoinRequests(requests: JoinRequest[], now: number): JoinRequest[] {
  return requests.filter((req) => req.state === 'pending' || isRecentlyDecided(req, now));
}
